import { Component, OnInit, Input } from '@angular/core';
import { CeRequestOptions, RequestHeaderComponent } from '../../request-header/request-header.component';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { PmoProjectsQueryModel, PmoProgramService, PmoProgram } from '../../../shared/services/pmo-program.service';
import { addDays } from 'date-fns';
import { DateService } from '../../../shared/services/date.service';
import { asEnumerable } from 'linq-es2015';
import { RequestDesign } from '../../../shared/services/request.service';
import { CurrentUser } from '../../../shared/services/current-user.service';
import { ValidatorsService } from '../../../shared/services/validators.service';
import { Settings } from '../../../settings';
import { SettingsService } from 'research.web.settings';

@Component({
  selector: 'app-experiment-details-form',
  templateUrl: './experiment-details-form.component.html'
})
export class ExperimentDetailsFormComponent implements OnInit {
  
  @Input('options') ceRequestOptions: CeRequestOptions;
  @Input('experimentDetailsFormGroup') experimentDetailsFormGroup: FormGroup;
  @Input('requestDesign') requestDesign: RequestDesign;
  @Input('currentUser') currentUser: CurrentUser;

  public ceSites: CeSiteDisplay[];
  public crops: CropDisplay[];
  public requestPurposes: RequestPurposeDisplay[];
  public productConcepts: ProductConceptDisplay[];
  public pmoPrograms: PmoProgramDisplay[];
  public pmoProjects: PmoProjectDisplay[];
  public delegates: DelegateDisplay[];
  public disableCropSelection: boolean = false;
  public settings: Settings;
  public showColdTreatmentInput:boolean= false;
  public get selectedCrop() { return this.experimentDetailsFormGroup.get("crop") };
  public get plantingDate() { return this.experimentDetailsFormGroup.get("plantingDate") };
  public get coldTreatmentDays(){return this.experimentDetailsFormGroup.get("weeksOfColdTreatment")}
  public get averageDaysForSeedMaturity() { 
    if(this.selectedCrop.value)
      return this.ceRequestOptions.crops.find(c => c.id === this.selectedCrop.value).averageDaysForSeedMaturity 
    else
      return 1;
  }
  
  public get earliestSeedPacketDate() { 
    if(this.selectedCrop.value && this.plantingDate.value) 
    {            
       if(this.isWheatCrop && this.coldTreatmentDays != null)
       {        
        return addDays(this.plantingDate.value, this.averageDaysForSeedMaturity + (this.coldTreatmentDays.value * 7) + this.settings.NumberOfDaysInDormancy);
       }
       return addDays(this.plantingDate.value, this.averageDaysForSeedMaturity);
    }

    return addDays(this.plantingDate.value, 1);
  }

  public get isWheatCrop(): boolean {
    return this.selectedCrop && this.selectedCrop.value === this.settings.wheatCropId;
  }

  constructor(
    private pmoProgramService: PmoProgramService,
    private dateService: DateService,
    private validatorsService: ValidatorsService,
    private settingsService: SettingsService) { 
      this.settings = this.settingsService.get<Settings>("_appSettings");
    }

  ngOnInit() {

    let activeCeSites = this.ceRequestOptions.ceSites.filter(s => s.isActive);
    if (activeCeSites.length === 1) {
      this.pmoProjects = null;
      this.experimentDetailsFormGroup.patchValue({
        ceSite: this.ceRequestOptions.ceSites.find(s => s.isActive).id
      });
    }

    if (this.requestDesign && (this.requestDesign.requestedSeed.length > 0 || this.requestDesign.numberOfSeedsPerCell !== 1)) {
      this.disableCropSelection = true;
    }
    else {
      this.disableCropSelection = null;
    }

    this.formatOptions();
    this.subscriptions();
  }

  public showExperimentNameErrorMessages = (): boolean => {
    let name: FormControl = this.experimentDetailsFormGroup.get("name") as FormControl;
    return name.value && name.invalid && (name.dirty || name.touched);
  };

  public control = (controlName: string): FormControl => {
    return this.experimentDetailsFormGroup.get(controlName) as FormControl;
  };

  private formatOptions = (): void => {
    this.ceSites = this.ceRequestOptions.ceSites
      .filter(s => s.isActive)
      .sort(RequestHeaderComponent.selectListSorter)
      .map(s => <CeSiteDisplay>{ id: s.id, display: s.name });

    this.crops = this.ceRequestOptions.crops
      .filter(s => s.isActive)
      .sort(RequestHeaderComponent.selectListSorter)
      .map(s => <CropDisplay>{ id: s.id, display: s.name });

    this.requestPurposes = this.ceRequestOptions.requestPurposes
      .filter(s => s.isActive)
      .filter(s => this.currentUser.ceMember || s.name !== 'Donors')
      .sort(RequestHeaderComponent.selectListSorter)
      .map(c => <RequestPurposeDisplay>{ id: c.id, display: c.name });

    this.productConcepts = this.ceRequestOptions.productConcepts
      .filter(c => c.isActive)
      .sort(RequestHeaderComponent.selectListSorter)
      .map(c => <ProductConceptDisplay>{ id: c.id, display: c.name });

    this.pmoPrograms = this.ceRequestOptions.pmoPrograms
      .filter(p => p.isActive && p.activeProjectCount > 0)
      .sort(this.pmoProgramSorter)
      .map(
        p => <PmoProgramDisplay>{ id: p.programKey, display: p.pmoProgramName }
      );

    this.delegates = asEnumerable(this.ceRequestOptions.users.filter(x => x.requestMember === true))
      .Where(u => u.ceMember === this.currentUser.ceMember)
      .OrderBy(u => u.surname)
      .ThenBy(u => u.givenName)
      .Select(u => {
        return <DelegateDisplay>{
          id: u.id,
          display: `${u.givenName} ${u.surname}`
        };
      })
      .ToArray();
  };

  private subscriptions = (): void => {
    this.experimentDetailsFormGroup.get('pmoProgram')
      .valueChanges
      .subscribe(key => this.onProgramChange(key));

    this.experimentDetailsFormGroup.get('crop')
      .valueChanges
      .subscribe(d => {
        const packetDate = this.experimentDetailsFormGroup.get('seedPacketDate');
        packetDate.clearValidators();
        packetDate.setValidators([this.validatorsService.mustBeOnOrAfterDate(this.earliestSeedPacketDate), this.validatorsService.isValidDate, Validators.required]);
        packetDate.updateValueAndValidity();   
        if(d==this.settings.wheatCropId)
        {
          this.showColdTreatmentInput=true;
          this.coldTreatmentDays.clearValidators();
          this.coldTreatmentDays.setValidators([Validators.required, Validators.min(0)]);
          this.coldTreatmentDays.updateValueAndValidity();   
        } 
        else
        {
          this.showColdTreatmentInput=false;
          if(this.coldTreatmentDays)
          {
            this.coldTreatmentDays.clearValidators();
          this.coldTreatmentDays.setValue(null);
          }
        }
      });
      
      if(this.coldTreatmentDays)
      {
        this.coldTreatmentDays
      .valueChanges
      .subscribe(d => {
        const packetDate = this.experimentDetailsFormGroup.get('seedPacketDate');
        packetDate.clearValidators();
        packetDate.setValidators([this.validatorsService.mustBeOnOrAfterDate(this.earliestSeedPacketDate), this.validatorsService.isValidDate, Validators.required]);
        packetDate.updateValueAndValidity();
      });
    }

    this.experimentDetailsFormGroup.get('plantingDate')
      .valueChanges
      .subscribe(d => {
        const packetDate = this.experimentDetailsFormGroup.get('seedPacketDate');
        packetDate.clearValidators();
        packetDate.setValidators([this.validatorsService.mustBeOnOrAfterDate(this.earliestSeedPacketDate), this.validatorsService.isValidDate, Validators.required]);
        packetDate.updateValueAndValidity();
      });
  };

  private onProgramChange(programKey: string): void {
    this.pmoProjects = null;
    this.experimentDetailsFormGroup.patchValue({
      pmoProject: null
    });

    if (programKey != null) {
      let selectedPmoProgram = this.ceRequestOptions
        .pmoPrograms
        .find(
          o => o.programKey === programKey
        );

      let queryModel: PmoProjectsQueryModel = {
        pmoProgramKey: selectedPmoProgram.programKey
      };

      this.pmoProgramService
        .getProjects(queryModel)
        .subscribe(p => {
          this.pmoProjects = asEnumerable(p)
                              .Where(p => p.prismId !== 0)
                              .Where(p => p.isActive)
                              .Select(p => <PmoProjectDisplay>{
                                id: p.projectKey,
                                display: p.pmoProjectName
                              })
                              .ToArray()
      });
    }
  }

  public earliestPlantingDate = (): Date => {
    const d = this.dateService.getDateAfterXDays(12);
    const midnight = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
    return midnight;
  };

  private pmoProgramSorter = (i1: PmoProgram, i2: PmoProgram): number => {
    return i1.pmoProgramName.localeCompare(i2.pmoProgramName);
  };
}

interface DelegateDisplay {
  id: number;
  display: string;
}

interface CeSiteDisplay {
  id: number;
  display: string;
}

interface CropDisplay {
  id: number;
  display: string;
}

interface RequestPurposeDisplay {
  id: number;
  display: string;
}

interface ProductConceptDisplay {
  id: number;
  display: string;
}

interface PmoProgramDisplay {
  id: string;
  display: string;
}

interface PmoProjectDisplay {
  id: string;
  display: string;
}



// WEBPACK FOOTER //
// F:/PHAZP5152IS01-4/_work/202/s/src/app/request-creation/experiment-details/experiment-details-form/experiment-details-form.component.ts